import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { SortIcon } from '@/components/common/SortIcon';
import { DeleteDialog } from '@/components/common/Dialogs';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { reviewsQueryKeys } from '../api';
import { RatingStars } from './RatingStars';
import { CreateReviewDialog } from './CreateReviewDialog';
import type { Review, ReviewFilters } from '../types';

interface GetColumnsParams {
  orderBy: string;
  order: 'asc' | 'desc' | '';
  onSort: (field: string) => void;
  filters: ReviewFilters;
}

export function useReviewColumns({
  orderBy,
  order,
  onSort,
}: GetColumnsParams): ColumnDef<Review>[] {
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);

  const columns: ColumnDef<Review>[] = [
    {
      accessorKey: 'comment',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('comment')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('comment')}
        >
          <p>{t('comment')}</p>
          <SortIcon active={orderBy === 'comment'} direction={order} />
        </div>
      ),
      cell: ({ row }) => (
        <Link
          to={`/app/reviews/${row.original.id}`}
          className="text-blue-700 hover:underline"
        >
          {row.original.comment?.substring(0, 50)}
          {row.original.comment?.length > 50 ? '...' : ''}
        </Link>
      ),
    },
    {
      accessorKey: 'rating',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('rating')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('rating')}
        >
          <p>{t('rating')}</p>
          <SortIcon active={orderBy === 'rating'} direction={order} />
        </div>
      ),
      cell: ({ row }) => <RatingStars rating={row.original.rating} />,
    },
    {
      accessorKey: 'user',
      header: () => <p>{t('assignedTo')}</p>,
      cell: ({ row }) => (
        <Link
          to={`/app/users/${row.original.user?.id}`}
          className="text-blue-700 hover:underline"
        >
          {row.original.user?.name || '-'}
        </Link>
      ),
    },
    {
      accessorKey: 'matter',
      header: () => <p>{t('matter')}</p>,
      cell: ({ row }) => {
        const matter = row.original.matter;
        return matter?.id ? (
          <Link
            to={`/app/matters/${matter.id}`}
            className="text-blue-700 hover:underline"
          >
            {matter.name || `#${matter.id}`}
          </Link>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      },
    },
    {
      accessorKey: 'createdBy',
      header: () => <p>{t('createdBy')}</p>,
      cell: ({ row }) => {
        const createdBy = row.original.createdBy;
        return createdBy?.id ? (
          <Link
            to={`/app/users/${createdBy.id}`}
            className="text-blue-700 hover:underline"
          >
            {createdBy.name || `#${createdBy.id}`}
          </Link>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: () => <p>{t('createdAt')}</p>,
      cell: ({ row }) => {
        const dateStr = row.original.createdAt;
        return dateStr ? (
          <div>{format(new Date(dateStr), 'yyyy-MM-dd HH:mm')}</div>
        ) : (
          <span>-</span>
        );
      },
    },
  ];

  // Add actions column if user has permissions
  if (hasAccess('review', 'update') || hasAccess('review', 'delete')) {
    columns.splice(0, 0, {
      id: 'actions',
      header: t('actionsLabel'),
      cell: ({ row }) => {
        const data = row.original;

        return (
          <div className="flex">
            {hasAccess('review', 'update') && <CreateReviewDialog id={data.id} />}
            {hasAccess('review', 'delete') && (
              <DeleteDialog
                id={data.id}
                title={data.comment?.substring(0, 30) || `Review #${data.id}`}
                url="reviews"
                queryKey={reviewsQueryKeys.lists()}
              />
            )}
          </div>
        );
      },
      enableHiding: false,
    });
  }

  return columns;
}
